'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const processSteps = [
  {
    step: '01',
    title: 'Apply',
    desc: 'Submit your company details and fundraising requirements for review.',
  },
  {
    step: '02',
    title: 'Review',
    desc: 'We evaluate stage, sector, traction, and investor fit against our network.',
  },
  {
    step: '03',
    title: 'Match',
    desc: 'Relevant investors are identified based on mandate, thesis, and interest.',
  },
  {
    step: '04',
    title: 'Present',
    desc: 'You present directly to syndicate members actively deploying capital.',
  },
  {
    step: '05',
    title: 'Connect',
    desc: 'Interested investors reach out. You meet them one-on-one.',
  },
  {
    step: '06',
    title: 'Advance',
    desc: 'Conversations progress through due diligence toward investment.',
  },
];

const stats = [
  { value: '$1M+', label: 'Minimum raise to qualify' },
  { value: 'Biweekly', label: 'Investor roundtables' },
  { value: '2 Stages', label: 'Pre-seed & Seed focus' },
  { value: 'Global', label: 'Investor network reach' },
];

const investorTypes = [
  { icon: '◆', label: 'Angel Investors' },
  { icon: '◈', label: 'Syndicates' },
  { icon: '◉', label: 'Family Offices' },
  { icon: '◎', label: 'Venture Capital' },
  { icon: '◐', label: 'Strategic Investors' },
  { icon: '◑', label: 'Fund Managers' },
];

const qualifyingCriteria = [
  'Pre-seed or seed stage company',
  'Raising $1M or more',
  'Clear use of funds and deployment plan',
  'Defined market and business model',
  'Founding team with relevant experience',
  'Compelling investment narrative',
];

const roundtableTopics = [
  {
    title: 'Founder–Investor Sessions',
    desc: 'Direct conversations between founders and active investors in a structured setting.',
  },
  {
    title: 'Industry-Focused Events',
    desc: 'Sector-specific roundtables connecting founders with investors aligned to the space.',
  },
  {
    title: 'Syndicate Deal Reviews',
    desc: 'Investors review curated opportunities and provide feedback or express interest.',
  },
  {
    title: 'Network Building',
    desc: 'Informal sessions designed to build lasting relationships across the ecosystem.',
  },
];

function useScrollFade(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, [ref]);
}

export default function SyndicatePage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const processRef = useRef<HTMLDivElement>(null);
  const roundtableRef = useRef<HTMLDivElement>(null);
  const raiseRef = useRef<HTMLDivElement>(null);
  const howRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useScrollFade(heroRef);
  useScrollFade(statsRef);
  useScrollFade(processRef);
  useScrollFade(roundtableRef);
  useScrollFade(raiseRef);
  useScrollFade(howRef);
  useScrollFade(ctaRef);

  return (
    <main className="min-h-screen bg-atmospheric overflow-x-hidden">
      <div className="noise-overlay" />
      <Header />

      {/* ── HERO ── */}
      <section ref={heroRef} className="relative pt-36 pb-24 px-6 overflow-hidden">
        {/* Atmospheric blobs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] blob-primary opacity-30 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] blob-secondary opacity-20 pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Back to Home breadcrumb */}
          <div className="mb-8 scroll-fade">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-muted-foreground hover:text-primary transition-colors duration-200"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
          </div>

          <div className="max-w-4xl">
            <p className="section-label scroll-fade mb-5">Aregn Syndicate</p>
            <h1 className="text-hero-xl font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6 leading-none">
              Capital is personal.{' '}
              <span className="font-serif italic text-primary">Relationships matter.</span>
            </h1>
            <p className="text-lg text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 max-w-2xl mb-10">
              The Syndicate brings together investors interested in discovering and backing
              promising companies. Our objective is not simply to create introductions — it is to
              create <em>relevant</em> introductions.
            </p>
            <div className="flex flex-wrap gap-4 scroll-fade scroll-fade-delay-3">
              <Link
                href="/contact"
                className="px-8 py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300"
              >
                Explore Syndicate →
              </Link>
              <Link
                href="#how-it-works"
                className="px-8 py-4 rounded-full border border-primary/30 text-primary text-sm font-semibold tracking-widest uppercase hover:bg-primary/10 transition-all duration-300"
              >
                How It Works
              </Link>
            </div>
          </div>

          {/* Investor type pills */}
          <div className="mt-16 scroll-fade scroll-fade-delay-4">
            <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-5">
              A curated investor network connecting qualifying founders with
            </p>
            <div className="flex flex-wrap gap-3">
              {investorTypes.map((type) => (
                <div
                  key={type.label}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-sm text-foreground/80"
                >
                  <span className="text-primary text-xs">{type.icon}</span>
                  {type.label}
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm font-semibold text-primary tracking-wide">
              Meet investors. Build relationships. Raise capital.
            </p>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section ref={statsRef} className="py-12 px-6 border-y border-primary/10 bg-card/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className={`scroll-fade ${i > 0 ? `scroll-fade-delay-${i}` : ''} text-center`}
              >
                <div className="text-3xl font-semibold text-primary mb-1">{stat.value}</div>
                <div className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — PROCESS ── */}
      <section ref={processRef} id="how-it-works" className="py-24 px-6 border-b border-primary/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-label scroll-fade mb-5">The Process</p>
            <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-4">
              From application to{' '}
              <span className="font-serif italic text-primary">investor conversation.</span>
            </h2>
            <p className="text-sm text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2">
              Designed for qualifying founders who want ongoing access to the investor syndicate
              ecosystem.
            </p>
          </div>

          {/* Bento process grid — asymmetric */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 scroll-fade">
            {processSteps.map((item, i) => (
              <div
                key={item.step}
                className={`bento-card rounded-2xl p-7 flex flex-col gap-4 ${
                  i === 3 ? 'md:col-span-2' : i === 4 ? 'md:col-span-1' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-4xl font-semibold text-primary/20 font-sans leading-none">
                    {item.step}
                  </span>
                  <div className="w-2 h-2 rounded-full bg-primary/40 mt-2" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Flow diagram — horizontal connector */}
          <div className="mt-12 scroll-fade scroll-fade-delay-2">
            <div className="relative overflow-x-auto">
              <div className="flex items-center min-w-max mx-auto justify-center gap-0 py-4">
                {['Apply', 'Review', 'Match', 'Present', 'Connect', 'Advance'].map((label, i) => (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full border-2 border-primary/50 bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {i + 1}
                      </div>
                      <span className="text-xs text-muted-foreground mt-2 font-medium">
                        {label}
                      </span>
                    </div>
                    {i < 5 && (
                      <div className="w-12 h-px bg-gradient-to-r from-primary/40 to-primary/20 mb-5 mx-1" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INVESTOR ROUNDTABLES ── */}
      <section ref={roundtableRef} className="py-24 px-6 border-b border-primary/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="section-label scroll-fade mb-5">Investor Roundtables</p>
              <div className="eyebrow-badge scroll-fade scroll-fade-delay-1 mb-6">Biweekly</div>
              <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-2 mb-6">
                Meet the people who can help{' '}
                <span className="font-serif italic text-primary">move your company forward.</span>
              </h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-3 mb-8">
                Roundtables, founder-investor sessions, and industry-focused events designed to
                create meaningful connections — not just introductions.
              </p>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors scroll-fade scroll-fade-delay-4"
              >
                Enquire about roundtables
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {roundtableTopics.map((topic, i) => (
                <div
                  key={topic.title}
                  className={`bento-card rounded-2xl p-6 scroll-fade ${i > 0 ? `scroll-fade-delay-${Math.min(i, 4)}` : ''}`}
                >
                  <div className="w-8 h-px bg-primary mb-4" />
                  <h3 className="text-sm font-semibold text-foreground mb-2">{topic.title}</h3>
                  <p className="text-xs text-muted-foreground font-light leading-relaxed">
                    {topic.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── RAISING $1M+ ── */}
      <section ref={raiseRef} className="py-24 px-6 border-b border-primary/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            {/* Left — main content */}
            <div className="lg:col-span-3">
              <p className="section-label scroll-fade mb-5">For Founders</p>
              <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6">
                Raising <span className="font-serif italic text-primary">$1M+?</span>
              </h2>
              <p className="text-base text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 mb-6">
                If you&apos;re a pre-seed or seed stage founder preparing to raise $1M or more,
                Aregn Syndicate provides an opportunity to present your company directly to our
                investor syndicate ecosystem.
              </p>
              <p className="text-sm text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-3 mb-8">
                Founders present directly to members of the syndicate investor ecosystem who are
                interested in or actively deploying capital in your industry. You get to present as
                you receive interest from our syndicate — meet the investors, connect with them, and
                go into one-on-one conversations with those who want to learn more.
              </p>

              <div className="scroll-fade scroll-fade-delay-4">
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">
                  Qualifying criteria
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {qualifyingCriteria.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 py-2.5 border-b border-primary/8"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        className="text-primary shrink-0"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span className="text-sm text-foreground/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — highlight card */}
            <div className="lg:col-span-2 scroll-fade scroll-fade-delay-2">
              <div className="bento-card rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 blob-primary opacity-20 pointer-events-none" />
                <div className="relative z-10">
                  <div className="text-5xl font-semibold text-primary mb-2 font-sans">$1M+</div>
                  <p className="text-sm text-muted-foreground mb-8 font-light">
                    Minimum raise threshold to present to the syndicate
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-primary text-xs font-bold">1</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Present to the Syndicate
                        </p>
                        <p className="text-xs text-muted-foreground font-light mt-0.5">
                          Pitch directly to investors actively deploying capital in your sector.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-primary text-xs font-bold">2</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Receive Investor Interest
                        </p>
                        <p className="text-xs text-muted-foreground font-light mt-0.5">
                          Interested investors signal intent and request follow-up conversations.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-primary text-xs font-bold">3</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          One-on-One Conversations
                        </p>
                        <p className="text-xs text-muted-foreground font-light mt-0.5">
                          Meet investors individually to explore fit, answer questions, and build
                          relationships.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Link
                    href="/contact"
                    className="w-full block py-3.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase text-center hover:bg-accent transition-all duration-300"
                  >
                    Enquire Now
                  </Link>
                  <p className="text-xs text-muted-foreground text-center mt-3 font-light">
                    Pre-seed and seed-stage startups raising $1M+ can apply to present.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW WE WORK ── */}
      <section ref={howRef} className="py-24 px-6 border-b border-primary/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-label scroll-fade mb-5">How We Work</p>
            <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-4">
              Designed for founders who want{' '}
              <span className="font-serif italic text-primary">ongoing access.</span>
            </h2>
            <p className="text-sm text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2">
              Designed for qualifying founders who want ongoing access to the investor syndicate
              ecosystem — not a one-time introduction.
            </p>
          </div>

          {/* Asymmetric bento layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Large card */}
            <div className="md:col-span-2 bento-card rounded-3xl p-10 scroll-fade relative overflow-hidden">
              <div className="absolute bottom-0 right-0 w-64 h-64 blob-secondary opacity-30 pointer-events-none" />
              <div className="relative z-10">
                <div className="w-8 h-px bg-primary mb-6" />
                <h3 className="text-2xl font-semibold text-foreground mb-4">
                  You present. Investors engage.{' '}
                  <span className="font-serif italic text-primary">Conversations happen.</span>
                </h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6">
                  Unlike a cold introduction or a database submission, Aregn Syndicate puts you in
                  front of investors who are already interested in your space. You present your
                  company, investors signal interest, and you go into direct one-on-one
                  conversations with those who want to learn more.
                </p>
                <p className="text-sm text-muted-foreground font-light leading-relaxed">
                  The process is structured to create momentum — not just meetings. Every
                  conversation is a step toward a relationship, and every relationship is a step
                  toward capital.
                </p>
              </div>
            </div>

            {/* Right column — 2 stacked cards */}
            <div className="flex flex-col gap-4">
              <div className="bento-card rounded-2xl p-7 scroll-fade scroll-fade-delay-1 flex-1">
                <div className="text-3xl font-semibold text-primary/30 mb-3 font-sans">◈</div>
                <h3 className="text-base font-semibold text-foreground mb-2">
                  Relevant Introductions
                </h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">
                  Investors in our syndicate are matched by sector, stage, and thesis — not just
                  availability.
                </p>
              </div>
              <div className="bento-card rounded-2xl p-7 scroll-fade scroll-fade-delay-2 flex-1">
                <div className="text-3xl font-semibold text-primary/30 mb-3 font-sans">◉</div>
                <h3 className="text-base font-semibold text-foreground mb-2">Ongoing Access</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">
                  Access to the syndicate ecosystem extends beyond a single presentation —
                  relationships are built over time.
                </p>
              </div>
            </div>

            {/* Bottom row — 3 equal cards */}
            <div className="bento-card rounded-2xl p-7 scroll-fade scroll-fade-delay-1">
              <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-3">
                Step 1
              </p>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                Present to the Syndicate
              </h3>
              <p className="text-xs text-muted-foreground font-light leading-relaxed">
                Founders present directly to syndicate members deploying capital in their industry.
              </p>
            </div>
            <div className="bento-card rounded-2xl p-7 scroll-fade scroll-fade-delay-2">
              <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-3">
                Step 2
              </p>
              <h3 className="text-sm font-semibold text-foreground mb-2">Get Investor Interest</h3>
              <p className="text-xs text-muted-foreground font-light leading-relaxed">
                Interested investors signal intent. You know who wants to learn more before the
                first meeting.
              </p>
            </div>
            <div className="bento-card rounded-2xl p-7 scroll-fade scroll-fade-delay-3">
              <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-3">
                Step 3
              </p>
              <h3 className="text-sm font-semibold text-foreground mb-2">
                One-on-One Conversations
              </h3>
              <p className="text-xs text-muted-foreground font-light leading-relaxed">
                Meet investors individually, build relationships, and advance toward investment
                decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section ref={ctaRef} className="py-28 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-atmospheric pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] blob-primary opacity-20 pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="section-label scroll-fade mb-5">Aregn Syndicate</p>
          <h2 className="text-display font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6">
            To know more, <span className="font-serif italic text-primary">enquire now.</span>
          </h2>
          <p className="text-base text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 mb-10 max-w-xl mx-auto">
            Pre-seed and seed-stage startups raising $1M+ can apply to present at Aregn Syndicate.
            Get in touch to learn more about the process and how to qualify.
          </p>
          <div className="flex flex-wrap justify-center gap-4 scroll-fade scroll-fade-delay-3">
            <Link
              href="/contact"
              className="px-10 py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300"
            >
              Enquire Now
            </Link>
            <Link
              href="/contact"
              className="px-10 py-4 rounded-full border border-primary/30 text-primary text-sm font-semibold tracking-widest uppercase hover:bg-primary/10 transition-all duration-300"
            >
              Join Investor Network
            </Link>
          </div>
          <p className="text-xs text-muted-foreground mt-8 scroll-fade scroll-fade-delay-4 font-light max-w-lg mx-auto">
            Submission does not guarantee an investor introduction or investment. Opportunities are
            reviewed based on investor fit, mandate and availability.
          </p>
        </div>
      </section>
      <Footer />
    </main>
  );
}
